import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CommandPalette from 'components/ui/CommandPalette';
import Sidebar from 'components/ui/Sidebar';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import Loader from 'components/ui/Loader';
import useUser from 'hooks/useUser';
import { useUpdatePasswordMutation } from 'redux/features/users/userApi';
import { toast } from 'react-toastify';
import { useGetSalaryInformationMutation, useLazyGetPaySlipQuery } from 'redux/features/salaryApi';
import { useGetTaxesQuery } from 'redux/features/taxApi';
import SalaryBreakdownModal from 'pages/employee-time-logs/components/SalaryBreakdownModal';
import FamilyDetails from 'pages/employee-edit-full-page/components/FamilyDetails';
import { formatDate } from 'utils/common';
import PasswordInputModal from './components/PasswordInputModal';
import UpdatePasswordModal from './components/UpdatePasswordModal';

const EmployeeProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showSalaryInformation, setShowSalaryInformation] = useState(false);
  const [openUpdatePasswordModal, setOpenUpdatePasswordModal] = useState(false);
  const [updatePassword] = useUpdatePasswordMutation();
  const [openPasswordInputModal, setOpenPasswordInputModal] = useState(false);

  const salaryInformationDefault = {
    annual_salary: '0',
    monthlyIncentives: [],
    monthly_salary: '0',
    overtime_enabled: false,
    paid_leave: 0,
  };
  const [salaryInformation, setSalaryInformation] = useState(salaryInformationDefault);
  const [getSalaryInformation, { isLoading: isGettingSalaryInformation }] =
    useGetSalaryInformationMutation();

  // Payslip viewer state
  const now = new Date();
  const [payslipMonth, setPayslipMonth] = useState(now.getMonth() + 1);
  const [payslipYear, setPayslipYear] = useState(now.getFullYear());
  const [payslipData, setPayslipData] = useState(null);
  const [payslipNotFound, setPayslipNotFound] = useState(false);
  const [getPaySlip, { isFetching: isPayslipLoading }] = useLazyGetPaySlipQuery();
  const { data: taxesData } = useGetTaxesQuery();
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);

  const handleShowSalaryInformation = () => {
    if (showSalaryInformation) {
      // Reset state when closing
      setPayslipData(null);
      setShowBreakdownModal(false);
      setPayslipNotFound(false);
    }
    setOpenPasswordInputModal(!showSalaryInformation);
    setShowSalaryInformation(false);
  };

  const handleConfirmPassword = async (password) => {
    try {
      const response = await getSalaryInformation({ password }).unwrap();
      if (response?.data) {
        setOpenPasswordInputModal(false);
        setShowSalaryInformation(true);
        setSalaryInformation(response?.data);
        setTimeout(() => {
          setShowSalaryInformation(false);
          setSalaryInformation(salaryInformationDefault);
        }, 120000);
      }
    } catch {
      setShowSalaryInformation(false);
      setSalaryInformation(salaryInformationDefault);
      return false;
    }
  };
  const user = useUser();

  const handleViewPayslip = async () => {
    setPayslipNotFound(false);
    try {
      const result = await getPaySlip({
        month: payslipMonth,
        year: payslipYear,
        userId: user?.id,
      }).unwrap();
      if (result?.data) {
        setPayslipData(result.data);
        setShowBreakdownModal(true);
        setPayslipNotFound(false);
      } else {
        setPayslipData(null);
        setPayslipNotFound(true);
        setShowBreakdownModal(false);
      }
    } catch {
      setPayslipData(null);
      setPayslipNotFound(true);
      setShowBreakdownModal(false);
    }
  };

  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - i);
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-purple-100 text-purple-800';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800';
      case 'EMPLOYEE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) {
    return <Loader />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const getPeriodLabel = (type) =>
    ({
      INTERNSHIP: 'Internship',
      PROBATION: 'Probation',
      TRAINEE: 'Training',
      EMPLOYMENT: 'Employment',
      NOTICE: 'Notice Period',
    })[type] ||
    type ||
    'Unknown';

  const InfoField = ({ label, value, icon }) => (
    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      {icon && (
        <div className="flex-shrink-0 mt-1">
          <Icon name={icon} size={20} color="var(--color-primary)" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs select-none font-medium text-gray-500 uppercase tracking-wide mb-1">
          {label}
        </p>
        <p className="text-sm select-none font-semibold text-gray-900 break-words">
          {value || <span className="text-gray-400 italic">Not provided</span>}
        </p>
      </div>
    </div>
  );

  const handleUpdatePassword = async (formData) => {
    try {
      const response = await updatePassword({ payload: formData, id: user?.id }).unwrap();
      if (response.message) {
        toast.success('Password updated successfully');
        setOpenUpdatePasswordModal(false);
        return true;
      }
    } catch {
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-secondary-50">
      <Header />
      <Sidebar />
      <CommandPalette />
      <PasswordInputModal
        onClose={() => setOpenPasswordInputModal(false)}
        isOpen={openPasswordInputModal}
        onConfirm={handleConfirmPassword}
        isLoading={isGettingSalaryInformation}
        title="View Salary Information"
        description="To view the details, please enter your password to continue."
      />
      <UpdatePasswordModal
        onClose={() => setOpenUpdatePasswordModal(false)}
        isOpen={openUpdatePasswordModal}
        onConfirm={handleUpdatePassword}
        isLoading={isGettingSalaryInformation}
      />
      <main className="lg:ml-60 pt-16">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Profile Header Card */}
          <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
            <div className="bg-surface items-end border-b border-border">
              <div className="p-6 sm:p-8 flex flex-row items-end justify-between">
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
                      <span className="text-3xl sm:text-4xl font-bold text-white">
                        {user?.first_name?.[0]?.toUpperCase() || 'U'}
                        {user?.last_name?.[0]?.toUpperCase() || ''}
                      </span>
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="flex-1 text-center sm:text-left">
                    <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                      {user?.first_name} {user?.middle_name} {user?.last_name}
                    </h1>
                    <p className="text-sm sm:text-base text-text-secondary mb-3">{user?.email}</p>
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                      {user?.role && (
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(
                            user.role,
                          )}`}
                        >
                          {user.role.replace('_', ' ')}
                        </span>
                      )}
                      {user?.employee_id && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                          ID: {user.employee_id}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end ">
                  <button
                    onClick={() => setOpenUpdatePasswordModal(true)}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Update Password
                  </button>
                </div>
              </div>
              <div className="overflow-hidden">
                <div className="flex border-b border-border">
                  <button
                    onClick={() => setActiveTab('profile')}
                    className={`relative flex-1 px-6 py-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all outline-none focus:outline-none ${
                      activeTab === 'profile'
                        ? 'text-primary-700 bg-primary-50/50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon name="User" size={18} />
                    <span>Profile</span>
                    {activeTab === 'profile' && (
                      <motion.div
                        layoutId="activeTabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary-500"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('family')}
                    className={`relative flex-1 px-6 py-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all outline-none focus:outline-none ${
                      activeTab === 'family'
                        ? 'text-primary-700 bg-primary-50/50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon name="Users" size={18} />
                    <span>Family Details</span>
                    {activeTab === 'family' && (
                      <motion.div
                        layoutId="activeTabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary-500"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Family Details Tab */}
          {activeTab === 'family' && (
            <motion.div variants={itemVariants}>
              <FamilyDetails userId={user?.id} />
            </motion.div>
          )}

          {/* Profile Tab Content */}
          {activeTab === 'profile' && (
            <>
              {/* Personal Information */}
              <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
                <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-border bg-gray-50">
                    <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
                      <Icon name="User" size={24} color="var(--color-primary)" />
                      <span>Personal Information</span>
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField label="First Name" value={user?.first_name} icon="User" />
                      <InfoField label="Middle Name" value={user?.middle_name} icon="User" />
                      <InfoField label="Last Name" value={user?.last_name} icon="User" />
                      <InfoField
                        label="Date of Birth"
                        value={formatDate(user?.date_of_birth)}
                        icon="Calendar"
                      />
                      <InfoField label="Gender" value={user?.gender} icon="User" />
                      <InfoField label="Martial Status" value={user?.marital_status} icon="User" />
                      <InfoField label="College Name" value={user?.college_name} icon="BookOpen" />
                      <InfoField
                        label="Qualification"
                        value={user?.qualification}
                        icon="BookOpen"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Work Information */}
              <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
                <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-border bg-gray-50">
                    <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
                      <Icon name="Briefcase" size={24} color="var(--color-primary)" />
                      <span>Work Information</span>
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField
                        label="Department"
                        value={user?.department?.name || 'N/A'}
                        icon="Building"
                      />
                      <InfoField
                        label="Designation"
                        value={user?.designation?.name || 'N/A'}
                        icon="Award"
                      />
                      <InfoField label="Role" value={user?.role?.replace('_', ' ')} icon="Shield" />
                      <InfoField label="Employee ID" value={user?.employee_id} icon="Tag" />

                      {user?.employee_bonds.length > 0 && (
                        <React.Fragment>
                          <InfoField
                            label="Bond Start "
                            value={
                              user?.employee_bonds[0].start_date
                                ? formatDate(user?.employee_bonds[0].start_date)
                                : 'N/A'
                            }
                            icon="Calendar"
                          />
                          <InfoField
                            label="Bond End "
                            value={
                              user?.employee_bonds[0].end_date
                                ? formatDate(user?.employee_bonds[0].end_date)
                                : 'N/A'
                            }
                            icon="Calendar"
                          />
                        </React.Fragment>
                      )}
                      {user?.employee_periods?.map((period) => (
                        <React.Fragment key={period.id}>
                          <InfoField
                            label={`${getPeriodLabel(period.period_type)} Start `}
                            value={formatDate(period.start_date)}
                            icon="Calendar"
                          />
                          <InfoField
                            label={`${getPeriodLabel(period.period_type)} End `}
                            value={formatDate(period.end_date)}
                            icon="Calendar"
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Contact Information */}
              <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
                <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-border bg-gray-50">
                    <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
                      <Icon name="Mail" size={24} color="var(--color-primary)" />
                      <span>Contact Information</span>
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField label="Email Address" value={user?.email} icon="Mail" />
                      <InfoField label="Mobile Number" value={user?.mobile} icon="Phone" />
                      <InfoField
                        label="Emergency Contact"
                        value={user?.emergency_contact_number}
                        icon="Phone"
                      />
                      <InfoField label="City" value={user?.city} icon="MapPin" />
                      <InfoField
                        label="Current Address"
                        value={user?.current_address}
                        icon="MapPin"
                      />
                      <InfoField
                        label="Permanent Address"
                        value={user?.permanent_address}
                        icon="MapPin"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Additional Information */}
              {(salaryInformation?.paid_leave !== undefined ||
                salaryInformation?.salary ||
                salaryInformation?.monthlyIncentives) && (
                <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
                  <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b flex justify-between items-center border-border bg-gray-50">
                      <h2 className="text-md font-semibold text-text-primary flex items-center space-x-2">
                        <Icon name="Banknote" size={24} color="var(--color-primary)" />
                        <span>Salary Information</span>
                      </h2>
                      <div className="flex items-center space-x-3 select-none">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="overtime_enabled"
                            checked={showSalaryInformation}
                            onChange={handleShowSalaryInformation}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            {showSalaryInformation ? 'Hide' : 'Show'}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className={`p-6 ${!showSalaryInformation ? 'blur-sm' : ''}`}>
                      <div className={`grid grid-cols-1 md:grid-cols-2  gap-4`}>
                        {salaryInformation?.paid_leave !== undefined && (
                          <InfoField
                            label="Paid Leave Balance"
                            value={`${salaryInformation.paid_leave || 0} days`}
                            icon="Calendar"
                          />
                        )}
                        {salaryInformation?.annual_salary && (
                          <InfoField
                            label="Annual Salary"
                            value={`₹${salaryInformation.annual_salary.toLocaleString('en-IN')}`}
                            icon="IndianRupee"
                          />
                        )}
                        {salaryInformation?.monthly_salary && (
                          <InfoField
                            label="Monthly Salary"
                            value={`₹${salaryInformation.monthly_salary.toLocaleString('en-IN')}`}
                            icon="CreditCard"
                          />
                        )}
                        {salaryInformation?.overtime_enabled !== undefined && (
                          <InfoField
                            label="Overtime Enabled"
                            value={salaryInformation.overtime_enabled ? 'Yes' : 'No'}
                            icon="Clock"
                          />
                        )}
                      </div>
                      {salaryInformation?.monthlyIncentives &&
                        salaryInformation.monthlyIncentives.length > 0 && (
                          <div className="mt-6">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                              <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
                              <span>Monthly Incentives</span>
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {salaryInformation.monthlyIncentives.map((incentive, index) => (
                                <div
                                  key={index}
                                  className="p-3 bg-primary-50 border border-primary-200 rounded-lg"
                                >
                                  <p className="text-xs font-medium text-primary-700 mb-1">
                                    {incentive.title}
                                  </p>
                                  <p className="text-sm font-bold text-primary-900">
                                    ₹{incentive.amount?.toLocaleString('en-IN')}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Salary Breakdown Viewer */}
                    {showSalaryInformation && (
                      <div className="mt-6 border-t border-border p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                          <Icon name="FileText" size={20} color="var(--color-primary)" />
                          <span>Salary Breakdown</span>
                        </h3>
                        <div className="flex flex-wrap items-end gap-4">
                          {/* Month Selector */}
                          <div className="flex flex-col">
                            <label className="text-xs font-medium text-gray-700 mb-0.5 ml-1">
                              Month
                            </label>
                            <select
                              value={payslipMonth}
                              onChange={(e) => {
                                setPayslipMonth(Number(e.target.value));
                                setPayslipNotFound(false);
                                setShowBreakdownModal(false);
                              }}
                              className="min-w-[160px] h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition-all cursor-pointer shadow-sm"
                            >
                              {MONTH_NAMES.map((name, idx) => (
                                <option key={idx} value={idx + 1}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {/* Year Selector */}
                          <div className="flex flex-col">
                            <label className="text-xs font-medium text-gray-700 mb-0.5 ml-1">
                              Year
                            </label>
                            <select
                              value={payslipYear}
                              onChange={(e) => {
                                setPayslipYear(Number(e.target.value));
                                setPayslipNotFound(false);
                                setShowBreakdownModal(false);
                              }}
                              className="min-w-[120px] h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition-all cursor-pointer shadow-sm"
                            >
                              {yearOptions.map((yr) => (
                                <option key={yr} value={yr}>
                                  {yr}
                                </option>
                              ))}
                            </select>
                          </div>
                          {/* View Breakdown Button */}
                          <button
                            onClick={handleViewPayslip}
                            disabled={isPayslipLoading}
                            className={`h-10 px-6 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center space-x-2 shadow-md hover:shadow-lg active:scale-95`}
                          >
                            {isPayslipLoading ? (
                              <>
                                <Icon name="Loader" size={18} className="animate-spin" />
                                <span>Loading...</span>
                              </>
                            ) : (
                              <>
                                <Icon name="Eye" size={18} />
                                <span>View Breakdown</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Not Found Message */}
                        {payslipNotFound && (
                          <div className="mt-4 flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <Icon name="AlertCircle" size={20} color="#d97706" />
                            <p className="text-sm font-medium text-amber-700">
                              Salary not generated for {MONTH_NAMES[payslipMonth - 1]} {payslipYear}
                              .
                            </p>
                          </div>
                        )}

                        {/* Breakdown Modal */}
                        {showBreakdownModal && payslipData && (
                          <SalaryBreakdownModal
                            isOpen={showBreakdownModal}
                            onClose={() => setShowBreakdownModal(false)}
                            salaryData={payslipData}
                            selectedMonth={payslipMonth}
                            selectedYear={payslipYear}
                            employeeData={user}
                            taxes={taxesData?.data || []}
                            isReadOnlyOnly={true}
                            userRole={user?.role}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default EmployeeProfile;
